import $ from "jquery";

/**
 * ScrollToFixed - Fixed element on scroll
 * @param {string|jQuery} element - Selector or jQuery object
 * @param {Object} options - Configuration options
 * @param {number|Function} options.marginTop - Top margin when fixed (can be function)
 * @param {string} options.spacerClass - Class name for spacer element
 * @param {number} options.limit - Scroll limit to stop fixing
 * @param {Function} options.onFixed - Callback when element becomes fixed
 * @param {Function} options.onUnfixed - Callback when element becomes unfixed
 */
export function scrollToFixed(element, options = {}) {
    const $element = $(element);

    if (!$element.length) {
        console.warn("scrollToFixed: Element not found");
        return;
    }

    const defaults = {
        marginTop: 0,
        spacerClass: "scroll-to-fixed-spacer",
        limit: null,
        onFixed: null,
        onUnfixed: null,
    };

    const settings = { ...defaults, ...options };
    let elementOffset = $element.offset().top;
    let elementWidth = $element.outerWidth();
    let elementHeight = $element.outerHeight();

    function handleScroll() {
        const scrollTop = $(window).scrollTop();
        const marginTop =
            typeof settings.marginTop === "function"
                ? settings.marginTop()
                : settings.marginTop;
        const triggerPoint = elementOffset - marginTop;

        // Check if should be fixed
        const shouldFix = settings.limit
            ? scrollTop >= triggerPoint && scrollTop < settings.limit
            : scrollTop >= triggerPoint;

        if (shouldFix) {
            if (!$element.hasClass("scroll-to-fixed-fixed")) {
                // Create spacer to prevent layout shift
                $element.before(
                    `<div class="${settings.spacerClass}" style="height:${elementHeight}px"></div>`
                );

                // Apply fixed positioning
                $element.addClass("scroll-to-fixed-fixed").css({
                    position: "fixed",
                    top: marginTop + "px",
                    left: "50%",
                    transform: "translateX(round(to-zero, -50%, 1px))",
                    width: elementWidth + "px",
                    zIndex: 100,
                });

                // Trigger callback
                if (typeof settings.onFixed === "function") {
                    settings.onFixed($element);
                }
            }
        } else {
            if ($element.hasClass("scroll-to-fixed-fixed")) {
                // Remove spacer
                $element.prev(`.${settings.spacerClass}`).remove();

                // Reset styles
                $element.removeClass("scroll-to-fixed-fixed").css({
                    position: "",
                    top: "",
                    left: "",
                    transform: "",
                    width: "",
                    zIndex: "",
                });

                // Trigger callback
                if (typeof settings.onUnfixed === "function") {
                    settings.onUnfixed($element);
                }
            }
        }
    }

    function handleResize() {
        // Reset to get accurate measurements
        if ($element.hasClass("scroll-to-fixed-fixed")) {
            $element.prev(`.${settings.spacerClass}`).remove();
            $element.removeClass("scroll-to-fixed-fixed").css({
                position: "",
                top: "",
                left: "",
                transform: "",
                width: "",
                zIndex: "",
            });
        }

        // Recalculate dimensions
        elementOffset = $element.offset().top;
        elementWidth = $element.outerWidth();
        elementHeight = $element.outerHeight();

        // Reapply fixed if needed
        handleScroll();
    }

    $(window).on("scroll.scrollToFixed", handleScroll);
    $(window).on("resize.scrollToFixed", handleResize);
}

/**
 * Destroy scrollToFixed
 * @param {string|jQuery} element - Selector or jQuery object
 */
export function destroyScrollToFixed(element) {
    const $element = $(element);

    if ($element.hasClass("scroll-to-fixed-fixed")) {
        $element.prev(".scroll-to-fixed-spacer").remove();
        $element.removeClass("scroll-to-fixed-fixed").css({
            position: "",
            top: "",
            left: "",
            transform: "",
            width: "",
            zIndex: "",
        });
    }

    $(window).off("scroll.scrollToFixed resize.scrollToFixed");
}

export default scrollToFixed;
